import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Brain, Database, Cpu, Cloud, CheckCircle2, ArrowRight } from 'lucide-react';

const LevelCard = ({ icon: Icon, title, program, level, skills, eligibility, assessment }) => (
  <Card className="bg-[#111827] border-2 border-[#00E5FF]/20 hover:border-[#00E5FF]/40 transition-all overflow-hidden">
    <CardContent className="p-0">
      <div className="grid lg:grid-cols-3">
        <div className="p-8 flex flex-col items-center justify-center bg-gradient-to-br from-[#00E5FF]/10 to-[#1DE9B6]/5">
          <div className="w-32 h-32 rounded-full border-4 border-[#00E5FF]/30 flex items-center justify-center mb-4">
            <div className="w-24 h-24 rounded-full bg-[#00E5FF]/10 flex flex-col items-center justify-center">
              <Icon className="w-8 h-8 text-[#00E5FF] mb-1" />
              <span className="text-[#00E5FF] font-bold text-[10px] uppercase tracking-wider">Certified</span>
            </div>
          </div>
          <h3 className="text-white font-bold text-xl font-['Space_Grotesk'] text-center">{title}</h3>
          <span className="mt-2 px-4 py-1 rounded-full bg-[#00E5FF]/20 text-[#00E5FF] text-sm font-medium">{level} Level</span>
          <p className="text-[#9CA3AF]/50 text-xs mt-2">Trained & Industry Ready</p>
        </div>
        <div className="lg:col-span-2 p-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-[#00E5FF] font-bold text-sm uppercase tracking-wider mb-4">Skills Covered</h4>
              <ul className="space-y-2">
                {skills.map((skill) => (
                  <li key={skill} className="flex items-center gap-2 text-[#9CA3AF] text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#1DE9B6] flex-shrink-0" />
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <div>
                <h4 className="text-[#00E5FF] font-bold text-sm uppercase tracking-wider mb-3">Eligibility</h4>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{eligibility}</p>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-bold text-sm uppercase tracking-wider mb-3">Assessment</h4>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{assessment}</p>
              </div>
              <div>
                <h4 className="text-[#00E5FF] font-bold text-sm uppercase tracking-wider mb-3">Certificate ID Format</h4>
                <code className="text-[#1DE9B6] text-sm font-mono bg-[#0B0F1A] px-3 py-1.5 rounded-lg">CXAI-{program}-2026-XXXX</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const CertificationLevelsPage = () => {
  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />

      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-[#0B0F1A] to-[#020617]">
        <div className="absolute top-10 left-1/3 w-80 h-80 bg-[#00E5FF] rounded-full blur-[150px] opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk']" data-testid="levels-heading">
            Certification <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Levels</span>
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-3xl mx-auto">
            Progress through structured certification tiers. Each level validates increasing depth of skills and industry readiness.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div data-testid="cert-card-ai">
            <LevelCard
              icon={Brain} title="AI Associate" program="AI" level="Associate"
              skills={['Python for AI', 'Machine Learning Basics', 'Data Preprocessing', 'Neural Network Fundamentals', 'AI Ethics & Responsible AI']}
              eligibility="Graduates or final-year students in any discipline with basic programming knowledge."
              assessment="Online MCQ exam + Mini project submission + Viva evaluation"
            />
          </div>
          <div data-testid="cert-card-de">
            <LevelCard
              icon={Database} title="Data Engineer" program="DE" level="Professional"
              skills={['SQL & NoSQL Databases', 'ETL Pipelines', 'Apache Spark & Kafka', 'Data Warehousing', 'Cloud Data Services (AWS/GCP)']}
              eligibility="IT professionals or graduates with 6+ months experience in data or development roles."
              assessment="Theory exam + Hands-on pipeline project + Technical interview"
            />
          </div>
          <div data-testid="cert-card-ml">
            <LevelCard
              icon={Cpu} title="ML Engineer" program="ML" level="Professional"
              skills={['Advanced ML Algorithms', 'Deep Learning (TensorFlow/PyTorch)', 'MLOps & Model Deployment', 'Feature Engineering', 'NLP']}
              eligibility="Working professionals with 1+ year experience in ML/AI or CloudXAI AI Associate holders."
              assessment="Theory exam + End-to-end ML project + Live model demo + Panel interview"
            />
          </div>
          <div data-testid="cert-card-expert">
            <LevelCard
              icon={Cloud} title="AI Professional" program="AI" level="Expert"
              skills={['Generative AI & LLMs', 'AI System Architecture', 'Enterprise AI Strategy', 'Advanced Computer Vision', 'AI Solution Design']}
              eligibility="Senior professionals with 2+ years in AI/ML or CloudXAI ML Engineer certification holders."
              assessment="Advanced theory exam + Enterprise AI project + Architecture review + Expert panel"
            />
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 font-['Space_Grotesk'] text-white">Ready to Get Certified?</h2>
          <p className="text-[#9CA3AF] mb-8">Start your journey today. Already certified? Verify your credential.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/verify">
              <Button className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-base rounded-lg shadow-lg shadow-[#00E5FF]/40">
                Verify Certificate <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/certifications/digital-credentials">
              <Button className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-base rounded-lg bg-transparent">Digital Credentials</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CertificationLevelsPage;
